import { Container, Graphics, Text } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Info-tab payline map: one mini grid per line from live {@link gameConfig.paylines}.
 * Adapts automatically to 5×3 / 10 lines, 5×4 / 40 lines, or 5×4 / 100 lines after launch layout apply.
 *
 * @param {{
 *   width: number,
 *   columns?: number,
 *   mobile?: boolean,
 * }} opts
 * @returns {Container}
 */
export function createInfoPaylineDiagram({ width, columns, mobile = false } = {}) {
  const reels = Math.max(1, Number(gameConfig.reelCount) || 5);
  const rows = Math.max(1, Number(gameConfig.rowCount) || 3);
  const paylines = Array.isArray(gameConfig.paylines) ? gameConfig.paylines : [];
  const t = gameConfig.theme ?? {};

  const root = new Container();
  root.eventMode = 'none';
  if (!paylines.length || !(width > 0)) return root;

  const cols = Math.max(
    2,
    Math.min(
      Number.isFinite(columns) && columns > 0 ? Math.floor(columns) : mobile ? 4 : 5,
      paylines.length,
    ),
  );
  /** 100-line maps use more columns, so tighten the gaps to keep the section scrollable. */
  const dense = cols >= 8;
  const gapX = dense ? 6 : mobile ? 8 : 10;
  const gapY = dense ? 8 : mobile ? 10 : 12;
  const labelH = dense ? 11 : mobile ? 12 : 14;
  const cellGap = 1.5;
  const itemW = (width - gapX * (cols - 1)) / cols;

  /** Keep cells readable; 5×4 stays slightly shorter than 5×3 for the same width. */
  const gridInnerW = itemW * 0.92;
  const cellW = (gridInnerW - cellGap * (reels - 1)) / reels;
  const cellH = cellW * 0.92;
  const gridW = reels * cellW + (reels - 1) * cellGap;
  const gridH = rows * cellH + (rows - 1) * cellGap;
  const itemH = labelH + 4 + gridH;

  const activeFill = t.gold ?? 0xe7c463;
  const activeStroke = t.goldBright ?? 0xfff8dc;
  const idleFill = t.panelDeep ?? 0x1a0f0a;
  const idleStroke = t.panelBorder ?? 0x6b4423;
  const labelFill = t.goldBright ?? 0xfff8dc;

  paylines.forEach((line, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const item = new Container();
    item.eventMode = 'none';
    item.x = col * (itemW + gapX);
    item.y = row * (itemH + gapY);

    const lineNo = index + 1;
    const label = new Text({
      text: String(lineNo),
      style: {
        fontFamily: '"Montserrat", system-ui, sans-serif',
        fontSize: lineNo >= 100 ? (mobile ? 8 : 9) : mobile ? 10 : 11,
        fontWeight: '700',
        fill: labelFill,
        align: 'center',
      },
    });
    label.anchor.set(0.5, 0);
    label.x = itemW / 2;
    label.y = 0;
    label.eventMode = 'none';
    item.addChild(label);

    const grid = new Graphics();
    grid.eventMode = 'none';
    const gridX = (itemW - gridW) / 2;
    const gridY = labelH + 4;
    const path = Array.isArray(line) ? line : [];

    for (let reel = 0; reel < reels; reel++) {
      for (let r = 0; r < rows; r++) {
        const active = path[reel] === r;
        const x = gridX + reel * (cellW + cellGap);
        const y = gridY + r * (cellH + cellGap);
        grid
          .roundRect(x, y, cellW, cellH, Math.min(2.5, cellW * 0.2))
          .fill({ color: active ? activeFill : idleFill, alpha: active ? 0.95 : 0.85 })
          .stroke({
            width: active ? 1.25 : 1,
            color: active ? activeStroke : idleStroke,
            alpha: active ? 0.95 : 0.7,
          });
      }
    }
    item.addChild(grid);
    root.addChild(item);
  });

  const rowCount = Math.ceil(paylines.length / cols);
  root.hitArea = null;
  /** Useful for info-tab layout height without measuring after attach. */
  root.__infoPaylineHeight = rowCount * itemH + Math.max(0, rowCount - 1) * gapY;
  return root;
}
